import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowRight, CheckCircle2, LifeBuoy, Mail, MessageSquarePlus, Phone, Send } from "lucide-react";
import {
  TICKET_CATEGORIES,
  TICKET_STATUS_LABELS,
  categoryLabel,
  createTicket,
  getMyTicket,
  getMyTickets,
  replyToMyTicket,
  resolveMyTicket,
  supportErrorMessage,
} from "../api/support";

const inputClass =
  "w-full rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-800 placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/10";

const formatDateTime = (value) =>
  value
    ? new Intl.DateTimeFormat("fa-IR", { month: "long", day: "numeric", hour: "2-digit", minute: "2-digit" }).format(new Date(value))
    : "";

const ticketNumber = (value) => new Intl.NumberFormat("fa-IR", { useGrouping: false }).format(value ?? 0);

const StatusBadge = ({ status }) => {
  const meta = TICKET_STATUS_LABELS[status] || TICKET_STATUS_LABELS.open;
  return <span className={`shrink-0 rounded-md px-2 py-0.5 text-[11px] font-bold ${meta.className}`}>{meta.label}</span>;
};

// --- New ticket ------------------------------------------------------------------

function NewTicketForm({ onCreated, onCancel }) {
  const [form, setForm] = useState({ subject: "", category: "technical", body: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (event) => {
    event.preventDefault();
    if (!form.subject.trim()) return setError("موضوع را بنویسید.");
    if (!form.body.trim()) return setError("مشکل یا سؤالتان را توضیح دهید.");
    setBusy(true);
    setError("");
    try {
      onCreated(await createTicket(form));
    } catch (createError) {
      setError(supportErrorMessage(createError, "ارسال تیکت ناموفق بود."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-4 p-5">
      <div>
        <h2 className="text-base font-extrabold text-gray-900">تیکت جدید</h2>
        <p className="mt-1 text-xs text-gray-500">کارشناسان پشتیبانی معمولاً در ساعات اداری همان روز پاسخ می‌دهند.</p>
      </div>
      <div className="grid gap-3 sm:grid-cols-[1fr_12rem]">
        <label className="block">
          <span className="mb-1 block text-xs font-semibold text-gray-600">موضوع</span>
          <input
            autoFocus
            value={form.subject}
            maxLength={150}
            onChange={(e) => setForm({ ...form, subject: e.target.value })}
            placeholder="مثال: عکس غذاها در منو نمایش داده نمی‌شود"
            className={`${inputClass} h-11`}
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-semibold text-gray-600">نوع درخواست</span>
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={`${inputClass} h-11`}>
            {TICKET_CATEGORIES.map((category) => (
              <option key={category.value} value={category.value}>{category.label}</option>
            ))}
          </select>
        </label>
      </div>
      <label className="block">
        <span className="mb-1 block text-xs font-semibold text-gray-600">توضیحات</span>
        <textarea
          rows={6}
          value={form.body}
          maxLength={5000}
          onChange={(e) => setForm({ ...form, body: e.target.value })}
          placeholder="چه اتفاقی افتاد؟ در کدام صفحه؟ هر جزئیاتی که کمک کند مشکل را سریع‌تر پیدا کنیم."
          className={`${inputClass} py-2.5 leading-6`}
        />
      </label>
      {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-xs font-medium text-red-700" role="alert">{error}</p>}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={busy}
          className="inline-flex min-h-10 items-center gap-2 rounded-xl bg-primary px-5 text-sm font-bold text-white hover:bg-[#e8731f] disabled:opacity-60"
        >
          <Send size={16} aria-hidden="true" />
          {busy ? "در حال ارسال…" : "ارسال تیکت"}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel} className="min-h-10 rounded-xl px-4 text-sm font-bold text-gray-500 hover:bg-gray-100">
            انصراف
          </button>
        )}
      </div>
    </form>
  );
}

// --- Conversation ----------------------------------------------------------------

function TicketThread({ ticketId, onTicketChanged }) {
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const endRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setState("loading");
    setReply("");
    setError("");
    getMyTicket(ticketId)
      .then((result) => {
        if (cancelled) return;
        setData(result);
        setState("ready");
        onTicketChanged(result.ticket);
      })
      .catch(() => !cancelled && setState("error"));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
  }, [data?.messages?.length]);

  const send = async (event) => {
    event.preventDefault();
    if (!reply.trim()) return;
    setBusy(true);
    setError("");
    try {
      const result = await replyToMyTicket(ticketId, reply);
      setData((current) => ({ ticket: result.ticket, messages: [...current.messages, result.message] }));
      onTicketChanged(result.ticket);
      setReply("");
    } catch (sendError) {
      setError(supportErrorMessage(sendError, "ارسال پاسخ ناموفق بود."));
    } finally {
      setBusy(false);
    }
  };

  const resolve = async () => {
    setBusy(true);
    try {
      const ticket = await resolveMyTicket(ticketId);
      setData((current) => ({
        ticket,
        messages: [...current.messages, { _id: "resolved", authorType: "system", body: "شما این تیکت را حل‌شده اعلام کردید.", createdAt: new Date() }],
      }));
      onTicketChanged(ticket);
    } catch (resolveError) {
      setError(supportErrorMessage(resolveError, "بستن تیکت ناموفق بود."));
    } finally {
      setBusy(false);
    }
  };

  if (state === "loading") return <div className="m-5 h-64 animate-pulse rounded-xl bg-gray-100" aria-busy="true" />;
  if (state === "error") return <p className="p-8 text-center text-sm text-red-700">تیکت پیدا نشد.</p>;

  const { ticket, messages } = data;
  const resolved = ticket.status === "resolved";

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-gray-100 px-5 py-4">
        <div className="min-w-0">
          <p className="text-xs text-gray-400">
            تیکت #{ticketNumber(ticket.number)} · {categoryLabel(ticket.category)}
          </p>
          <h2 className="mt-1 text-base font-extrabold text-gray-900">{ticket.subject}</h2>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={ticket.status} />
          {!resolved && (
            <button
              type="button"
              onClick={resolve}
              disabled={busy}
              className="inline-flex min-h-8 items-center gap-1 rounded-lg border border-gray-200 px-2.5 text-xs font-bold text-gray-600 hover:bg-gray-50 disabled:opacity-50"
            >
              <CheckCircle2 size={14} aria-hidden="true" /> مشکلم حل شد
            </button>
          )}
        </div>
      </div>

      <ol className="flex flex-1 flex-col gap-3 overflow-y-auto bg-gray-50/60 px-5 py-4">
        {messages.map((message) =>
          message.authorType === "system" ? (
            <li key={message._id} className="text-center text-xs text-gray-400">{message.body}</li>
          ) : (
            <li
              key={message._id}
              className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-7 ${
                message.authorType === "restaurant"
                  ? "self-start rounded-tr-sm bg-white text-gray-800 ring-1 ring-gray-200"
                  : "self-end rounded-tl-sm bg-secondary_1 text-gray-800"
              }`}
            >
              <p className="mb-1 text-[11px] font-bold text-gray-500">
                {message.authorType === "restaurant" ? "شما" : `${message.authorName || "پشتیبانی"} · پشتیبانی بامیزبان`}
                <span className="mr-2 font-normal text-gray-400">{formatDateTime(message.createdAt)}</span>
              </p>
              <p className="whitespace-pre-wrap break-words">{message.body}</p>
            </li>
          )
        )}
        <li ref={endRef} aria-hidden="true" />
      </ol>

      <form onSubmit={send} className="border-t border-gray-100 p-4">
        {resolved && <p className="mb-2 text-xs text-gray-500">این تیکت بسته شده است. اگر پاسخ دهید، دوباره باز می‌شود.</p>}
        {error && <p className="mb-2 rounded-lg bg-red-50 px-3 py-2 text-xs font-medium text-red-700" role="alert">{error}</p>}
        <div className="flex items-end gap-2">
          <textarea
            rows={2}
            value={reply}
            maxLength={5000}
            onChange={(e) => setReply(e.target.value)}
            placeholder="پاسخ خود را بنویسید…"
            aria-label="پاسخ"
            className={`${inputClass} flex-1 resize-none py-2.5 leading-6`}
          />
          <button
            type="submit"
            disabled={busy || !reply.trim()}
            className="inline-flex h-11 shrink-0 items-center gap-2 rounded-xl bg-primary px-4 text-sm font-bold text-white hover:bg-[#e8731f] disabled:opacity-50"
          >
            <Send size={16} aria-hidden="true" /> ارسال
          </button>
        </div>
      </form>
    </div>
  );
}

// --- Page ------------------------------------------------------------------------

export default function SupportPage() {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [listState, setListState] = useState("loading");
  const [composing, setComposing] = useState(false);

  const load = useCallback(() => {
    setListState("loading");
    getMyTickets()
      .then((list) => {
        setTickets(list);
        setListState("ready");
      })
      .catch(() => setListState("error"));
  }, []);

  useEffect(load, [load]);

  const showForm = composing || (!ticketId && listState === "ready" && tickets.length === 0);
  const detailOpen = Boolean(ticketId) || showForm;

  const upsertTicket = (ticket) =>
    setTickets((current) => {
      const rest = current.filter((t) => t._id !== ticket._id);
      return [{ ...ticket, unread: false }, ...rest].sort((a, b) => new Date(b.lastMessageAt) - new Date(a.lastMessageAt));
    });

  const startNew = () => {
    setComposing(true);
    navigate("/dashboard/support");
  };

  return (
    <div className="space-y-4" dir="rtl">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-extrabold text-gray-900">پشتیبانی</h1>
          <p className="mt-0.5 text-sm text-gray-500">سؤال یا مشکلی دارید؟ تیکت بفرستید تا کارشناسان ما پیگیری کنند.</p>
        </div>
        <button
          type="button"
          onClick={startNew}
          className="inline-flex min-h-10 items-center gap-2 rounded-xl bg-primary px-4 text-sm font-bold text-white hover:bg-[#e8731f]"
        >
          <MessageSquarePlus size={17} aria-hidden="true" /> تیکت جدید
        </button>
      </header>

      <div className="grid gap-4 lg:grid-cols-[20rem_minmax(0,1fr)]">
        {/* Ticket list (hidden on mobile while a ticket or the form is open) */}
        <aside className={`flex flex-col gap-4 ${detailOpen ? "hidden lg:flex" : ""}`}>
          <section className="rounded-2xl border border-gray-200 bg-white" aria-label="تیکت‌های شما">
            <h2 className="border-b border-gray-100 px-4 py-3 text-sm font-bold text-gray-800">تیکت‌های شما</h2>
            {listState === "loading" ? (
              <div className="space-y-2 p-3" aria-busy="true">
                {[0, 1, 2].map((i) => <div key={i} className="h-14 animate-pulse rounded-xl bg-gray-100" />)}
              </div>
            ) : listState === "error" ? (
              <div className="p-6 text-center text-sm text-red-700">
                دریافت تیکت‌ها ناموفق بود.{" "}
                <button type="button" onClick={load} className="font-bold underline">دوباره</button>
              </div>
            ) : tickets.length === 0 ? (
              <p className="p-6 text-center text-xs leading-6 text-gray-500">هنوز تیکتی ثبت نکرده‌اید.</p>
            ) : (
              <ul className="max-h-[32rem] divide-y divide-gray-100 overflow-y-auto">
                {tickets.map((ticket) => (
                  <li key={ticket._id}>
                    <Link
                      to={`/dashboard/support/${ticket._id}`}
                      onClick={() => setComposing(false)}
                      aria-current={ticket._id === ticketId ? "page" : undefined}
                      className={`block px-4 py-3 transition-colors hover:bg-gray-50 ${ticket._id === ticketId ? "bg-primary_1/60" : ""}`}
                    >
                      <div className="flex items-center gap-2">
                        {ticket.unread && <span className="h-2 w-2 shrink-0 rounded-full bg-primary" aria-label="پاسخ جدید" />}
                        <p className={`flex-1 truncate text-sm ${ticket.unread ? "font-extrabold text-gray-900" : "font-semibold text-gray-700"}`}>
                          {ticket.subject}
                        </p>
                        <StatusBadge status={ticket.status} />
                      </div>
                      <p className="mt-1 text-xs text-gray-400">
                        #{ticketNumber(ticket.number)} · {formatDateTime(ticket.lastMessageAt)}
                      </p>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="rounded-2xl border border-gray-200 bg-white p-4 text-sm" aria-label="راه‌های تماس">
            <h2 className="mb-3 flex items-center gap-2 font-bold text-gray-800">
              <LifeBuoy size={16} className="text-secondary" aria-hidden="true" /> راه‌های دیگر تماس
            </h2>
            <p className="flex items-center gap-2 text-gray-600">
              <Phone size={15} className="text-gray-400" aria-hidden="true" />
              <span dir="ltr">0930 234 7789</span>
            </p>
            <p className="mt-2 flex items-center gap-2 text-gray-600">
              <Mail size={15} className="text-gray-400" aria-hidden="true" />
              <span dir="ltr">support@bamizban.com</span>
            </p>
          </section>
        </aside>

        {/* Detail */}
        <section
          className={`flex min-h-[30rem] flex-col rounded-2xl border border-gray-200 bg-white ${detailOpen ? "" : "hidden lg:flex"}`}
          aria-label="جزئیات تیکت"
        >
          {detailOpen && (
            <Link
              to="/dashboard/support"
              onClick={() => setComposing(false)}
              className="flex items-center gap-1 border-b border-gray-100 px-4 py-2.5 text-xs font-bold text-gray-500 lg:hidden"
            >
              <ArrowRight size={14} aria-hidden="true" /> همه تیکت‌ها
            </Link>
          )}
          {showForm ? (
            <NewTicketForm
              onCancel={tickets.length ? () => setComposing(false) : null}
              onCreated={(ticket) => {
                upsertTicket(ticket);
                setComposing(false);
                navigate(`/dashboard/support/${ticket._id}`);
              }}
            />
          ) : ticketId ? (
            <TicketThread ticketId={ticketId} onTicketChanged={upsertTicket} />
          ) : (
            <div className="flex flex-1 flex-col items-center justify-center gap-2 p-10 text-center">
              <LifeBuoy size={24} className="text-gray-300" aria-hidden="true" />
              <p className="text-sm font-semibold text-gray-600">یک تیکت را از فهرست انتخاب کنید.</p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
